/**
 * Outer (and hole) contours of a set of unit tiles. Each cell (x, y) fills
 * [x, x + 1] × [y, y + 1]. Loops are grid corners: counterclockwise when the
 * tiles are on the left of the walk, clockwise for holes.
 */
import type { Vec2 } from '../model/format.ts';

export function outlineCells(cells: Iterable<[number, number]>): Vec2[][] {
  const filled = new Set<string>();
  for (const [x, y] of cells) filled.add(`${x},${y}`);
  if (!filled.size) return [];
  const has = (x: number, y: number) => filled.has(`${x},${y}`);
  type Edge = { a: Vec2; b: Vec2; used: boolean };
  const edges: Edge[] = [];
  const from = new Map<string, Edge[]>();
  const add = (a: Vec2, b: Vec2) => {
    const e: Edge = { a, b, used: false };
    edges.push(e);
    const k = `${a[0]},${a[1]}`;
    const list = from.get(k);
    if (list) list.push(e);
    else from.set(k, [e]);
  };
  for (const key of filled) {
    const [xs, ys] = key.split(',');
    const x = Number(xs), y = Number(ys);
    if (!has(x, y - 1)) add([x, y], [x + 1, y]);
    if (!has(x + 1, y)) add([x + 1, y], [x + 1, y + 1]);
    if (!has(x, y + 1)) add([x + 1, y + 1], [x, y + 1]);
    if (!has(x - 1, y)) add([x, y + 1], [x, y]);
  }
  const loops: Vec2[][] = [];
  for (const start of edges) {
    if (start.used) continue;
    const loop: Vec2[] = [start.a];
    let cur = start;
    cur.used = true;
    for (let n = 0; n <= edges.length; n++) {
      if (cur.b[0] === loop[0][0] && cur.b[1] === loop[0][1]) break;
      loop.push(cur.b);
      const next = (from.get(`${cur.b[0]},${cur.b[1]}`) ?? []).find((e) => !e.used);
      if (!next) break;
      next.used = true;
      cur = next;
    }
    const simple = simplify(loop);
    if (simple.length >= 3) loops.push(simple);
  }
  return loops;
}

function simplify(ring: Vec2[]): Vec2[] {
  if (ring.length < 3) return ring;
  const out: Vec2[] = [];
  for (let i = 0; i < ring.length; i++) {
    const prev = ring[(i + ring.length - 1) % ring.length];
    const cur = ring[i];
    const next = ring[(i + 1) % ring.length];
    const dx1 = cur[0] - prev[0], dy1 = cur[1] - prev[1];
    const dx2 = next[0] - cur[0], dy2 = next[1] - cur[1];
    if (dx1 * dy2 === dy1 * dx2) continue;
    out.push(cur);
  }
  return out.length >= 3 ? out : ring;
}
